// Basis Pengetahuan, read-only, for the user app: the tab "Basis Pengetahuan"
// of Regulasi (screens/Regulations.tsx; it was the Library page before the
// Library became the user's own documents). The admin maintains it in the
// admin console, admin/Knowledge.tsx.
//   kb_lead     plain spacing    how many files Pluto may cite
//   kb_toolbar  divided section  search and category, the same toolbar treatment as the other registers
//   kb_errors   notice           files that failed to load: file name and the loader's message
//   kb_drive    rows             documents synced from the admin's Google Drive folder, each with Unduh (the original file)
//   kb_files    rows             grouped by category (Regulasi, Templat, Panduan, Contoh), each group its own
//                                heading and count, so the register reads as a library and not one list
// Design review dropped the category index rail (relevance 1.43, under 1.5): the group headings carry it.
import { useState } from "react";
import * as API from "../api";
import * as Sync from "../knowledge-sync-api";
import { formatCount } from "../lib/format";
import { KNOWLEDGE_CATEGORY_LABEL, TAHAP_LABEL, TAHAP_ORDER } from "../lib/labels";
import { EmptyState, Notice, SearchInput, SectionHead, SkeletonRows, useAsync } from "../ui/components";
import { Glyph } from "../ui/icons";

const CATEGORY_ORDER = ["regulasi", "template", "panduan", "contoh"];

const CATEGORY_LEAD: Record<string, string> = {
  regulasi: "Peraturan yang boleh dirujuk Pluto. Nomor peraturan di luar daftar ini tidak dipakai.",
  template: "Susunan baku dokumen yang diikuti draf.",
  panduan: "Cara menyusun dan memeriksa dokumen.",
  contoh: "Contoh dokumen jadi sebagai acuan gaya.",
};

function tahapText(tahap: string[]): string {
  if (!tahap || tahap.length === 0) return "Semua tahap";
  return tahap.map((t) => ((TAHAP_ORDER as string[]).includes(t) ? TAHAP_LABEL[t as API.Tahap] : t)).join(", ");
}

export function groupFiles(files: API.KnowledgeFile[]): { category: string; files: API.KnowledgeFile[] }[] {
  const cats = [...new Set(files.map((f) => f.category))].sort((a, b) => {
    const ia = CATEGORY_ORDER.indexOf(a);
    const ib = CATEGORY_ORDER.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b, "id");
  });
  return cats.map((category) => ({
    category,
    files: files.filter((f) => f.category === category).sort((a, b) => a.title.localeCompare(b.title, "id")),
  }));
}

/** The Basis Pengetahuan register, as the body of the Regulasi tab. */
export function KnowledgeBrowser() {
  const kb = useAsync(() => API.getKnowledge(), []);
  // Synced from Google Drive; the bundled list keeps loading when this fails.
  const drive = useAsync(() => Sync.listLibraryDriveFiles().catch(() => [] as Sync.LibraryDriveFile[]), []);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");

  // Generated drive/... entries are shown once, as their Drive original (with Unduh).
  const all = (kb.data?.files ?? []).filter((f) => !f.file.startsWith("drive/"));
  const driveAll = drive.data ?? [];
  const s = q.trim().toLowerCase();
  const shown = all.filter((f) => (!category || f.category === category) && (!s || f.title.toLowerCase().includes(s) || f.file.toLowerCase().includes(s)));
  const groups = groupFiles(shown);
  const driveShown = driveAll.filter((f) => (!category || f.category === category) && (!s || f.title.toLowerCase().includes(s) || f.name.toLowerCase().includes(s)));
  const categories = [...new Set([...groupFiles(all).map((g) => g.category), ...driveAll.map((f) => f.category).filter((c): c is string => !!c)])];
  const errors = kb.data?.errors ?? [];

  return (
    <>
      <p className="quiet-line kb-lead">
        {kb.data ? `${formatCount(all.length + driveAll.length)} berkas dimuat. Pluto hanya merujuk isi dari berkas ini.` : "Memuat berkas pengetahuan"}
      </p>

      <section className="toolbar" aria-label="Cari berkas">
        <div className="field-row" data-cols="3">
          <div className="field">
            <label htmlFor="kb-q">Cari berkas</label>
            <SearchInput id="kb-q" value={q} onChange={setQ} placeholder="Judul atau nama berkas" />
          </div>
          <div className="field">
            <label htmlFor="kb-cat">Kategori</label>
            <select id="kb-cat" value={category} onChange={(e) => setCategory(e.currentTarget.value)}>
              <option value="">Semua kategori</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {KNOWLEDGE_CATEGORY_LABEL[c] ?? c}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {errors.length > 0 ? (
        <Notice tone="danger" title={`${formatCount(errors.length)} berkas gagal dimuat`}>
          <p>Berkas ini dilewati dan tidak dipakai Pluto sampai isinya diperbaiki.</p>
          <ul className="error-list">
            {errors.map((e) => (
              <li key={e.file}>
                <span className="mono">{e.file}</span>
                <span>{e.message}</span>
              </li>
            ))}
          </ul>
        </Notice>
      ) : null}

      {kb.loading && !kb.data ? (
        <SkeletonRows count={6} label="Memuat berkas" />
      ) : kb.error ? (
        <Notice tone="danger" title="Basis pengetahuan belum dapat dimuat" action={<button type="button" className="btn btn-secondary" onClick={kb.reload}>Muat ulang</button>}>
          <p>{API.errorMessage(kb.error)}</p>
        </Notice>
      ) : all.length === 0 && driveAll.length === 0 ? (
        <EmptyState icon="bookOpen" title="Belum ada berkas pengetahuan" body="Administrator belum menambahkan berkas rujukan." />
      ) : groups.length === 0 && driveShown.length === 0 ? (
        <EmptyState icon="filter" title="Tidak ada berkas yang cocok" body="Ubah kata kunci atau pilih semua kategori." />
      ) : (
        <div className="kb-groups">
          {driveShown.length > 0 ? (
            <section className="region kb-group" aria-labelledby="kb-drive">
              <SectionHead id="kb-drive" title={`Dari Google Drive (${formatCount(driveShown.length)})`} lead="Dokumen yang diunggah administrator. Unduh untuk membuka berkas aslinya." />
              <ul className="rows">
                {driveShown.map((f) => (
                  <li key={f.driveId} className="row">
                    <span className="row-main">
                      <span className="row-title">{f.title}</span>
                      <span className="row-meta">{f.category ? (KNOWLEDGE_CATEGORY_LABEL[f.category] ?? f.category) : "Tanpa kategori"}</span>
                    </span>
                    <span className="row-side">
                      <a className="btn btn-secondary" href={Sync.libraryDriveDownloadUrl(f.driveId)} download aria-label={`Unduh ${f.title}`}>
                        <Glyph name="documentDownload" />
                        <span>Unduh</span>
                      </a>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {groups.map((g) => (
            <section key={g.category} className="region kb-group" aria-labelledby={`kb-${g.category}`}>
              <SectionHead
                id={`kb-${g.category}`}
                title={`${KNOWLEDGE_CATEGORY_LABEL[g.category] ?? g.category} (${formatCount(g.files.length)})`}
                lead={CATEGORY_LEAD[g.category]}
              />
              <ul className="rows">
                {g.files.map((f) => (
                  <li key={f.id} className="row">
                    <span className="row-main">
                      <span className="row-title">{f.title}</span>
                      <span className="row-meta">{tahapText(f.tahap)}</span>
                    </span>
                    <span className="row-side">
                      <span className="row-meta">
                        <span className="num">{f.sectionCount}</span> bagian
                      </span>
                      <span className="row-meta mono">{f.file}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </>
  );
}
